import { useState, useEffect } from 'react';
import { api } from '@/lib/api';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Loader2, Plus, Edit, Trash2, Eye, EyeOff, Save, X } from 'lucide-react';

interface BlogPost {
  id: number;
  slug: string;
  title: string;
  excerpt: string;
  content: string;
  category: string;
  author: string;
  tags: string[];
  read_time: string;
  published: boolean;
  published_at: string | null;
  created_at: string;
  updated_at: string;
}

const emptyPost = {
  slug: '',
  title: '',
  excerpt: '',
  content: '',
  category: 'General',
  author: 'Douglas Bothma',
  tags: [] as string[],
  read_time: '',
  published: false,
};

export const BlogManager = () => {
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<Partial<BlogPost> | null>(null);
  const [isNew, setIsNew] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [tagInput, setTagInput] = useState('');

  useEffect(() => {
    fetchPosts();
  }, []);

  const fetchPosts = async () => {
    try {
      setLoading(true);
      const data = await api.get<BlogPost[]>('/blog/admin/all');
      setPosts(data || []);
    } catch (err) {
      setError('Failed to load blog posts');
    } finally {
      setLoading(false);
    }
  };

  const handleNew = () => {
    setEditing({ ...emptyPost });
    setIsNew(true);
    setError('');
  };

  const handleEdit = (post: BlogPost) => {
    setEditing({ ...post });
    setIsNew(false);
    setError('');
  };

  const handleCancel = () => {
    setEditing(null);
    setIsNew(false);
    setTagInput('');
  };

  const handleSave = async () => {
    if (!editing) return;
    try {
      setSaving(true);
      setError('');

      if (isNew) {
        await api.post('/blog/admin', editing);
      } else {
        await api.put(`/blog/admin/${editing.id}`, editing);
      }

      setEditing(null);
      setIsNew(false);
      setTagInput('');
      await fetchPosts();
    } catch (err: any) {
      setError(err?.response?.data?.error?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!confirm('Delete this blog post? This cannot be undone.')) return;
    try {
      await api.delete(`/blog/admin/${id}`);
      await fetchPosts();
    } catch (err) {
      setError('Failed to delete');
    }
  };

  const handleTogglePublish = async (post: BlogPost) => {
    try {
      await api.put(`/blog/admin/${post.id}`, { published: !post.published });
      await fetchPosts();
    } catch (err) {
      setError('Failed to update');
    }
  };

  const addTag = () => {
    if (!tagInput.trim() || !editing) return;
    const currentTags = editing.tags || [];
    if (!currentTags.includes(tagInput.trim())) {
      setEditing({ ...editing, tags: [...currentTags, tagInput.trim()] });
    }
    setTagInput('');
  };

  const removeTag = (tag: string) => {
    if (!editing) return;
    setEditing({ ...editing, tags: (editing.tags || []).filter(t => t !== tag) });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-[#E8651A]" />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-[#f5f5f5]">Blog Manager</h1>
        <Button onClick={handleNew} className="bg-[#E8651A] hover:bg-[#C9520E] text-white">
          <Plus className="w-4 h-4 mr-2" /> New Post
        </Button>
      </div>

      {error && (
        <div className="bg-red-900/20 border border-red-800 text-red-300 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      {/* Editor Panel */}
      {editing && (
        <Card className="mb-6 border-[#333] bg-[#111]">
          <CardHeader>
            <CardTitle className="text-[#f5f5f5]">
              {isNew ? 'New Blog Post' : 'Edit Blog Post'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-[#888] mb-1">Title *</label>
                  <input
                    type="text"
                    value={editing.title || ''}
                    onChange={e => setEditing({ ...editing, title: e.target.value })}
                    className="w-full bg-[#1a1a1a] border border-[#333] rounded px-3 py-2 text-[#f5f5f5] text-sm"
                    placeholder="Post title"
                  />
                </div>
                <div>
                  <label className="block text-sm text-[#888] mb-1">Slug *</label>
                  <input
                    type="text"
                    value={editing.slug || ''}
                    onChange={e => setEditing({ ...editing, slug: e.target.value.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '') })}
                    className="w-full bg-[#1a1a1a] border border-[#333] rounded px-3 py-2 text-[#f5f5f5] text-sm font-mono"
                    placeholder="post-slug"
                  />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm text-[#888] mb-1">Category</label>
                  <input
                    type="text"
                    value={editing.category || ''}
                    onChange={e => setEditing({ ...editing, category: e.target.value })}
                    className="w-full bg-[#1a1a1a] border border-[#333] rounded px-3 py-2 text-[#f5f5f5] text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm text-[#888] mb-1">Author</label>
                  <input
                    type="text"
                    value={editing.author || ''}
                    onChange={e => setEditing({ ...editing, author: e.target.value })}
                    className="w-full bg-[#1a1a1a] border border-[#333] rounded px-3 py-2 text-[#f5f5f5] text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm text-[#888] mb-1">Read Time</label>
                  <input
                    type="text"
                    value={editing.read_time || ''}
                    onChange={e => setEditing({ ...editing, read_time: e.target.value })}
                    className="w-full bg-[#1a1a1a] border border-[#333] rounded px-3 py-2 text-[#f5f5f5] text-sm"
                    placeholder="5 min read"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm text-[#888] mb-1">Excerpt</label>
                <textarea
                  value={editing.excerpt || ''}
                  onChange={e => setEditing({ ...editing, excerpt: e.target.value })}
                  className="w-full bg-[#1a1a1a] border border-[#333] rounded px-3 py-2 text-[#f5f5f5] text-sm h-20"
                  placeholder="Short description for listings..."
                />
              </div>

              <div>
                <label className="block text-sm text-[#888] mb-1">Tags</label>
                <div className="flex gap-2 mb-2 flex-wrap">
                  {(editing.tags || []).map(tag => (
                    <span key={tag} className="inline-flex items-center gap-1 bg-[#E8651A]/20 text-[#E8651A] px-2 py-1 rounded text-xs">
                      {tag}
                      <button onClick={() => removeTag(tag)} className="hover:text-white"><X className="w-3 h-3" /></button>
                    </span>
                  ))}
                </div>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={tagInput}
                    onChange={e => setTagInput(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addTag())}
                    className="flex-1 bg-[#1a1a1a] border border-[#333] rounded px-3 py-2 text-[#f5f5f5] text-sm"
                    placeholder="Add tag and press Enter"
                  />
                  <Button onClick={addTag} variant="outline" size="sm">Add</Button>
                </div>
              </div>

              <div>
                <label className="block text-sm text-[#888] mb-1">Content (HTML) *</label>
                <textarea
                  value={editing.content || ''}
                  onChange={e => setEditing({ ...editing, content: e.target.value })}
                  className="w-full bg-[#1a1a1a] border border-[#333] rounded px-3 py-2 text-[#f5f5f5] text-sm font-mono h-64"
                  placeholder="<h2>Section Title</h2><p>Content here...</p>"
                />
              </div>

              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2 text-sm text-[#888]">
                  <input
                    type="checkbox"
                    checked={editing.published || false}
                    onChange={e => setEditing({ ...editing, published: e.target.checked })}
                    className="rounded"
                  />
                  Published
                </label>
              </div>

              <div className="flex gap-3 pt-2">
                <Button onClick={handleSave} disabled={saving} className="bg-[#E8651A] hover:bg-[#C9520E] text-white">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                  {isNew ? 'Create' : 'Save Changes'}
                </Button>
                <Button onClick={handleCancel} variant="outline">
                  <X className="w-4 h-4 mr-2" /> Cancel
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Posts List */}
      <div className="space-y-3">
        {posts.length === 0 ? (
          <div className="text-center text-[#666] py-12">
            No blog posts yet. Click "New Post" to create one.
          </div>
        ) : (
          posts.map(post => (
            <div key={post.id} className="flex items-center gap-4 bg-[#111] border border-[#222] rounded-lg p-4">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="text-[#f5f5f5] font-medium truncate">{post.title}</h3>
                  {post.published ? (
                    <span className="text-green-500 text-xs bg-green-500/10 px-2 py-0.5 rounded">Published</span>
                  ) : (
                    <span className="text-yellow-500 text-xs bg-yellow-500/10 px-2 py-0.5 rounded">Draft</span>
                  )}
                </div>
                <div className="text-xs text-[#666]">
                  {post.slug} · {post.category} · {post.read_time || '—'} · {new Date(post.updated_at).toLocaleDateString()}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => handleTogglePublish(post)}
                  className="p-2 text-[#888] hover:text-[#f5f5f5] transition-colors"
                  title={post.published ? 'Unpublish' : 'Publish'}
                >
                  {post.published ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
                <button
                  onClick={() => handleEdit(post)}
                  className="p-2 text-[#888] hover:text-[#f5f5f5] transition-colors"
                  title="Edit"
                >
                  <Edit className="w-4 h-4" />
                </button>
                <button
                  onClick={() => handleDelete(post.id)}
                  className="p-2 text-[#888] hover:text-red-400 transition-colors"
                  title="Delete"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};
